import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Card from '../../components/Card';
import PlatformConnectionCard from '../../components/PlatformConnectionCard';
import ConnectPlatformCard from '../../components/ConnectPlatformCard';
import FacebookPageSelectorModal from '../../components/FacebookPageSelectorModal';
import {
  type PlatformConnection,
  getConnections,
  createConnection,
  verifyConnection,
  deleteConnection,
} from '../../lib/api';

// Available platforms configuration
const AVAILABLE_PLATFORMS = [
  {
    platform: 'linkedin_company',
    name: 'LinkedIn Company',
    description: 'Connect your LinkedIn Company Page to post updates and share content',
    color: 'bg-[#0A66C2]',
    icon: 'in',
    enabled: true,
  },
  {
    platform: 'linkedin_personal',
    name: 'LinkedIn Personal',
    description: 'Connect your personal LinkedIn profile to share updates',
    color: 'bg-[#0A66C2]',
    icon: 'in',
    enabled: true,
  },
  {
    platform: 'facebook_page',
    name: 'Facebook Page',
    description: 'Connect your Facebook Page to publish posts and engage with followers',
    color: 'bg-[#1877F2]',
    icon: 'fb',
    enabled: true,
  },
  {
    platform: 'instagram_business',
    name: 'Instagram Business',
    description: 'Connect your Instagram Business account to share photos and videos',
    color: 'bg-[#E4405F]',
    icon: 'ig',
    enabled: true,
  },
  {
    platform: 'youtube',
    name: 'YouTube',
    description: 'Connect your YouTube channel to upload videos',
    color: 'bg-[#FF0000]',
    icon: 'yt',
    enabled: true,
  },
  {
    platform: 'twitter',
    name: 'Twitter/X',
    description: 'Connect your Twitter/X account to post tweets',
    color: 'bg-black',
    icon: 'x',
    enabled: false,
    comingSoon: true,
  },
  {
    platform: 'tiktok',
    name: 'TikTok',
    description: 'Connect your TikTok account to share short videos',
    color: 'bg-black',
    icon: 'tk',
    enabled: false,
    comingSoon: true,
  },
  {
    platform: 'pinterest',
    name: 'Pinterest',
    description: 'Connect your Pinterest account to share pins',
    color: 'bg-[#E60023]',
    icon: 'pt',
    enabled: false,
    comingSoon: true,
  },
  {
    platform: 'wordpress',
    name: 'WordPress',
    description: 'Connect your WordPress site to publish blog posts',
    color: 'bg-[#21759B]',
    icon: 'wp',
    enabled: false,
    comingSoon: true,
  },
];

export default function ApiConnectionsSettingsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Two-step OAuth modal state
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [pendingConnectionId, setPendingConnectionId] = useState<string>('');
  const [pendingToken, setPendingToken] = useState<string>('');
  const [pendingPlatform, setPendingPlatform] = useState<'facebook_page' | 'instagram_business'>('facebook_page');

  useEffect(() => {
    loadConnections();

    // Check for OAuth callback
    const success = searchParams.get('success');
    const platform = searchParams.get('platform');
    const connectionId = searchParams.get('connectionId');
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (success === 'true' && connectionId && token) {
      // Two-step OAuth flow (Facebook/Instagram) - show page selector
      setPendingConnectionId(connectionId);
      setPendingToken(token);
      setPendingPlatform(platform as 'facebook_page' | 'instagram_business');
      setShowPageSelector(true);

      // Clear URL params
      setSearchParams({});
    } else if (success === 'true' && connectionId) {
      // Single-step OAuth completed
      setSuccessMessage(t('api.connection.messages.connected'));
      loadConnections();

      // Clear URL params
      setSearchParams({});
    } else if (errorParam) {
      // OAuth error
      setError(errorParam);

      // Clear URL params
      setSearchParams({});
    }
  }, []);

  const loadConnections = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await getConnections();
      setConnections(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (platform: string) => {
    setIsConnecting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await createConnection({ platform });

      if (response.authorizationUrl) {
        // OAuth flow - redirect to authorization URL
        window.location.href = response.authorizationUrl;
      } else if (response.id) {
        // Direct connection (e.g., WordPress) - success
        setSuccessMessage(t('api.connection.messages.connected'));
        loadConnections();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToConnect'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleVerify = async (connectionId: string) => {
    setVerifyingId(connectionId);
    setError('');
    setSuccessMessage('');

    try {
      const result = await verifyConnection(connectionId);

      if (result.isValid) {
        setSuccessMessage(t('api.connection.messages.verified'));
        loadConnections();
      } else {
        setError(t('api.connection.messages.verificationFailed'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToVerify'));
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    setDisconnectingId(connectionId);
    setError('');
    setSuccessMessage('');

    try {
      await deleteConnection(connectionId);
      setSuccessMessage(t('api.connection.messages.disconnected'));
      loadConnections();
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToDisconnect'));
    } finally {
      setDisconnectingId(null);
    }
  };

  const handlePageSelectorSuccess = () => {
    setSuccessMessage(t('api.connection.messages.connected'));
    loadConnections();
  };

  // Get connected platform IDs for filtering
  const connectedPlatformIds = connections.map((c) => c.platform);

  // Filter available platforms
  const availablePlatforms = AVAILABLE_PLATFORMS.filter(
    (p) => !connectedPlatformIds.includes(p.platform)
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b-2 border-black dark:border-gray-700 pb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('api.connection.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('api.connection.description')}
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-400 p-4">
          <p className="text-green-800 dark:text-green-200 font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 p-4">
          <p className="text-red-800 dark:text-red-200 font-medium">{error}</p>
        </div>
      )}

      {/* Connected Platforms */}
      {connections.length > 0 && (
        <Card>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('api.connection.connectedPlatforms')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connections.map((connection) => (
              <PlatformConnectionCard
                key={connection.id}
                connection={connection}
                onVerify={handleVerify}
                onDisconnect={handleDisconnect}
                isVerifying={verifyingId === connection.id}
                isDisconnecting={disconnectingId === connection.id}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Available Platforms */}
      {availablePlatforms.length > 0 && (
        <Card>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('api.connection.availablePlatforms')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availablePlatforms.map((platform) => (
              <ConnectPlatformCard
                key={platform.platform}
                name={platform.name}
                description={platform.description}
                color={platform.color}
                icon={platform.icon}
                onClick={() => handleConnect(platform.platform)}
                disabled={isConnecting || !platform.enabled}
                comingSoon={platform.comingSoon}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-black dark:border-gray-600 dark:border-t-white"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {t('api.connection.loading')}
          </p>
        </div>
      )}

      {/* No Platforms Connected Yet */}
      {!isLoading && connections.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔌</div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t('api.connection.noPlatforms')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('api.connection.noPlatformsDescription')}
            </p>
          </div>
        </Card>
      )}

      {/* Facebook/Instagram Page Selector Modal (Two-Step OAuth) */}
      <FacebookPageSelectorModal
        isOpen={showPageSelector}
        onClose={() => setShowPageSelector(false)}
        connectionId={pendingConnectionId}
        token={pendingToken}
        platform={pendingPlatform}
        onSuccess={handlePageSelectorSuccess}
      />
    </div>
  );
}
